import { useToasts } from "@certn/cascadia";
import {
  BasicButton,
  DangerButton,
  ButtonGroup,
  LegacyModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Link,
} from "@certn/cascadia-react";
import { APIError, useAPIRequest, usePeopleAPI } from "@certn/legacy-api";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useOverlayTriggerState } from "react-stately";

type DeclineModalProps = {
  applicantId: string;
  show: boolean;
  onClose: () => void;
  teamName?: string | null;
};

export function DeclineModal({
  applicantId,
  show,
  onClose,
  teamName,
}: DeclineModalProps) {
  const { t } = useTranslation();
  const modalState = useOverlayTriggerState({});
  const { show: showToast } = useToasts();
  const request = useAPIRequest();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const { mutate } = usePeopleAPI("/internal/people/applicant/list/");

  const declineRequest = async () => {
    setIsLoading(true);
    try {
      await request(
        `/internal/people/applicant/${applicantId}/decline-request/`,
        {
          method: "POST",
        }
      );
      showToast({
        title: t("Check successfully declined."),
        type: "info",
      });
      await mutate();
      onClose();
    } catch (e) {
      if (e instanceof APIError) {
        if (e.detail === "id_verification_started") {
          setShowErrorMessage(true);
        } else {
          showToast({
            title: t("Something went wrong, try again"),
            type: "error",
          });
        }
      }
    }
    setIsLoading(false);
  };

  if (!show) {
    return null;
  }
  return (
    <ModalOverlay state={modalState}>
      <LegacyModal>
        <ModalHeader close={onClose} title={t("Decline request")} />
        <ModalContent>
          {showErrorMessage ? (
            <div>
              <Text style="form/body-primary" color="text-weak">
                <Trans>
                  You are unable to decline this request as you have started the
                  Identity Verification session. Please contact{" "}
                  <Link
                    linkStyle="default"
                    textColor="text-accent-tofino"
                    href="mailto: support@certn.co"
                    testID="support-email-link"
                    target="_blank"
                  >
                    support@certn.co
                  </Link>
                </Trans>
              </Text>
            </div>
          ) : (
            <>
              <div>
                <Text style="form/body-primary" color="text-weak">
                  <Trans>
                    If you've decided not to proceed with the background check
                    for {{ teamName }} select the “Decline request“ button
                    below. Once confirmed, {{ teamName }} will be notified and
                    the check will be cancelled.
                  </Trans>
                </Text>
              </div>
              <br />
              <br />
              <div>
                <Text style="form/body-primary" color="text-weak">
                  <Trans>Please note that this action cannot be undone.</Trans>
                </Text>
              </div>
            </>
          )}
        </ModalContent>
        <ModalFooter>
          {!showErrorMessage && (
            <ButtonGroup>
              <BasicButton size="medium" onPress={onClose}>
                <Trans>Go back</Trans>
              </BasicButton>
              <DangerButton
                testID="decline-request-button"
                size="medium"
                onPress={declineRequest}
                isDisabled={showErrorMessage}
                isLoading={isLoading}
              >
                <Trans>Decline request</Trans>
              </DangerButton>
            </ButtonGroup>
          )}
        </ModalFooter>
      </LegacyModal>
    </ModalOverlay>
  );
}
